import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NullValidationHandler, OAuthErrorEvent, OAuthService, OAuthSuccessEvent } from 'angular-oauth2-oidc';
import { environment } from 'env/environment';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { authConfig } from 'src/assets/auth-config';
import { UserSecurityInfoModel } from 'types/user.interface';
import { API_BACKOFFICE_USER } from './api.service';
import { ToastService } from './toast.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VersionResponse } from 'types/version.interface';

export const AUTH_TOKEN_KEY = 'token';
export const REFRESH_TOKEN_KEY = 'refreshToken';
export const USER_SECURITY_INFO_KEY = 'userSecurityInfo';
export const STATUS_FORBIDDEN = 403;

const OIDC_PROVIDER = 'azuread';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  headers: any;

  constructor(
    private router: Router,
    private oauthService: OAuthService,
    private http: HttpClient,
    private toastService: ToastService,
    private modalService: NgbModal
  ) {
    this.redirectOnCallback();
    // For SSO logout
    this.oauthService.events.pipe(filter((e) => e.type === 'session_changed')).subscribe(() => {
      this.logout();
    });

    this.headers = new HttpHeaders({
      Authorization: `Bearer ${this.getBearerToken()}`
    });
  }

  getUserSecurityInfo(): Observable<UserSecurityInfoModel> {
    return this.http.get<UserSecurityInfoModel>(`${environment.apiUrl}${API_BACKOFFICE_USER}/security-info`, { headers: this.headers });
  }

  public getVersionInfo() {
    return this.http.get<VersionResponse>(`${environment.apiUrl}/version`, { headers: this.headers });
  }

  public fetchAndSaveVersionInfo(): void {
    this.getVersionInfo().subscribe({
      next: (version) => {
        localStorage.setItem('versionInfo', JSON.stringify(version));
      },
      error: (err) => {
        console.error('Error fetching version info:', err);
      }
    });
  }

  public async fetchUserSecurityInfo(): Promise<void> {
    return new Promise((resolve) => {
      this.getUserSecurityInfo().subscribe({
        next: (userInfo) => {
          localStorage.setItem(USER_SECURITY_INFO_KEY, JSON.stringify(userInfo));
          resolve();
        },
        error: (err) => {
          console.error('Error fetching user security info:', err);
          if (err.status === STATUS_FORBIDDEN) {
            this.toastService.showDanger('inactiveAccount', '', { shouldTranslate: true });
            this.logout();
          }
          resolve();
        }
      });
    });
  }

  async configure() {
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage);
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.tokenValidationHandler = new NullValidationHandler();

    await this.oauthService.loadDiscoveryDocumentAndTryLogin();

    if (this.isAuthenticated()) {
      if (window.location.pathname === '/login') {
        this.router.navigateByUrl(this.getRedirectUrl() ?? '/');
      }
    }
  }

  /**
   * Will hook into the OAuth 'token_recieved' event and perform a re-direct to the dashboard page.
   */
  public redirectOnCallback(): void {
    this.oauthService.events.subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        console.error(event);
      } else if (event instanceof OAuthSuccessEvent) {
        if (event.type === 'token_received') {
          if (!this.isAuthenticated()) {
            this.router.navigateByUrl('/');
          }
        }
      }
    });
  }

  login() {
    this.oauthService.initImplicitFlow(undefined, {
      //kc_idp_hint: OIDC_PROVIDER
    });
  }

  logout() {
    localStorage.removeItem(USER_SECURITY_INFO_KEY);
    sessionStorage.clear();
    this.oauthService.revokeTokenAndLogout(true);
    this.modalService.dismissAll();
    this.router.navigateByUrl('/login');
  }

  getUser(): Record<string, any> {
    return this.oauthService.getIdentityClaims();
  }

  isUserLoaded(): boolean {
    return !!localStorage.getItem(USER_SECURITY_INFO_KEY);
  }

  getUserId(): string {
    const userInfo = JSON.parse(localStorage.getItem(USER_SECURITY_INFO_KEY) || '{}');

    return userInfo?.userId || -1;
  }

  getUserName(): string | undefined {
    return this.getUser()['preferred_username'];
  }

  getUserFullName(): string | undefined {
    return this.getUser()['name'];
  }

  isAuthenticated(): boolean {
    return this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken();
  }

  getBearerToken(): string {
    return this.oauthService.getAccessToken();
  }

  userSecurityInfo(): UserSecurityInfoModel {
    return JSON.parse(localStorage.getItem(USER_SECURITY_INFO_KEY) || '{}');
  }

  setRedirectUrl(url: string) {
    sessionStorage.setItem('redirectUrl', url);
  }

  getRedirectUrl() {
    const url = sessionStorage.getItem('redirectUrl');
    sessionStorage.removeItem('redirectUrl');
    return url;
  }
}
